// apps/client/src/pages/leads/unsubscribes/UnsubscribesPage.tsx
//
// Lead Capture > Unsubscribes (/leads/unsubscribes).
// super_admin, administrator and marketing.
//
// For the person who asks to stop emails by phone, by replying, or in
// person: look up their exact address, see whether they are already
// unsubscribed and what they have consented to, and record the unsubscribe
// with a reason. Recording suppresses the address for every marketing email
// (eDM, sequences, welcome) and writes a consent withdrawal record.
//
// Exact-address search only: there is no list and no partial match, so the
// screen cannot be used to browse the audience.
//
// Spec: docs/plans/2026-10-23-WEBSITE-FORMS-PLATFORM.md §6 and §9.

import React, { useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { AlertTriangle, BellOff, CheckCircle2, MailX, Search, XCircle } from 'lucide-react';
import { CONSENT_PURPOSES, CONSENT_PURPOSE_LABELS } from '@valutten/shared-schemas/dist/consent';
import { PageHeader } from '../../../components/common/PageHeader';
import { Skeleton } from '../../../components/common/SkeletonLoader';
import { Spinner } from '../../../components/common/Spinner';
import {
  UNSUBSCRIBE_NOTE_MAX,
  UNSUBSCRIBE_REASONS,
  UNSUBSCRIBE_REASON_LABELS,
  useUnsubscribes,
  type UnsubscribeReason,
} from '../../../hooks/useUnsubscribes';

type OutletCtx = { userData?: { companyId?: string; role?: string } };

export const UNSUBSCRIBES_ROLES: readonly string[] = ['super_admin', 'administrator', 'marketing'];

const SUPPRESSION_REASON_LABELS: Readonly<Record<string, string>> = {
  unsubscribe: 'Used the unsubscribe link',
  unsubscribe_link: 'Used the unsubscribe link',
  complaint: 'Marked an email as spam',
  bounce: 'Address bounced',
  hard_bounce: 'Address bounced',
  manual: 'Recorded by your team',
  phone: 'Asked by phone',
  reply: 'Replied to an email',
  in_person: 'Asked in person',
  other: 'Other',
};

function suppressionReasonLabel(manualReason: string | null, reason: string | null, source: string | null): string {
  const key = manualReason ?? reason ?? source;
  if (!key) return 'Unsubscribed';
  return SUPPRESSION_REASON_LABELS[key] ?? key.replace(/[_-]+/g, ' ');
}

function formatWhen(ms: number | null): string {
  if (ms === null) return '';
  return new Date(ms).toLocaleDateString('en-AU', { timeZone: 'Australia/Brisbane', day: 'numeric', month: 'short', year: 'numeric' });
}

const UnsubscribesPage: React.FC = () => {
  const ctx = useOutletContext<OutletCtx>();
  const companyId = ctx.userData?.companyId;
  const allowed = UNSUBSCRIBES_ROLES.includes(ctx.userData?.role ?? '');
  const { status, searching, error, search, recordUnsubscribe, clear } = useUnsubscribes(allowed ? companyId : undefined);

  const [query, setQuery] = useState('');
  const [reason, setReason] = useState<UnsubscribeReason | ''>('');
  const [note, setNote] = useState('');
  const [recording, setRecording] = useState(false);
  const [recordError, setRecordError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const onSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setNotice(null);
    setRecordError(null);
    setReason('');
    setNote('');
    await search(query);
  };

  const onRecord = async () => {
    if (!status) return;
    if (!reason) {
      setRecordError('Choose how they asked to unsubscribe.');
      return;
    }
    setRecording(true);
    setRecordError(null);
    setNotice(null);
    try {
      const r = await recordUnsubscribe({ email: status.email, reason, note });
      setNotice(
        r.alreadySuppressed
          ? `${status.email} was already unsubscribed. Your note has been recorded.`
          : `${status.email} is unsubscribed from all marketing email.`,
      );
      setReason('');
      setNote('');
    } catch (err) {
      setRecordError(err instanceof Error ? err.message : 'The unsubscribe was not recorded.');
    } finally {
      setRecording(false);
    }
  };

  if (!allowed) {
    return (
      <div className="space-y-6">
        <PageHeader icon={BellOff} title="Unsubscribes" />
        <div className="min-h-[50vh] rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-amber-300 text-sm">
          You do not have permission to view this page.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <PageHeader
        icon={BellOff}
        title="Unsubscribes"
        subtitle="When someone asks you to stop emailing them, look up their address and record it here. They stop getting every marketing email straight away."
      />

      <form onSubmit={onSearch} className="flex flex-col sm:flex-row gap-2" role="search">
        <label htmlFor="unsub-email" className="sr-only">Email address</label>
        <input id="unsub-email" type="email" autoComplete="off" value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            if (status) clear();
          }}
          placeholder="name@example.com"
          className="flex-1 px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-sm" />
        <button type="submit" disabled={searching}
          className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-50">
          {searching ? <Spinner size="sm" /> : <Search className="w-4 h-4" />}
          Look up
        </button>
      </form>
      <p className="-mt-4 text-xs text-gray-500">Enter the full address exactly. Partial searches are not supported.</p>

      {error && (
        <div role="alert" className="rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-red-300 text-sm flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {notice && (
        <div role="status" className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-3 text-emerald-300 text-sm">{notice}</div>
      )}

      {searching && !status && <Skeleton height="10rem" />}

      {status && (
        <section aria-label="Subscriber status" className="rounded-lg border border-gray-700 bg-gray-800/40 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-700 flex items-start gap-3">
            {status.suppressed ? (
              <MailX className="w-5 h-5 text-amber-300 mt-0.5 flex-shrink-0" />
            ) : (
              <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
            )}
            <div>
              <p className="font-medium break-all">{status.email}</p>
              {status.suppressed ? (
                <>
                  <p className="text-sm text-amber-300">
                    Unsubscribed
                    {status.suppression
                      ? `: ${suppressionReasonLabel(status.suppression.manualReason, status.suppression.reason, status.suppression.source)}`
                      : ''}
                    {status.suppression?.atMs ? `, ${formatWhen(status.suppression.atMs)}` : ''}. No marketing email is sent to this address.
                  </p>
                  {status.suppression?.matchedOn === 'canonical_mailbox' && (
                    <p className="text-xs text-gray-400 mt-1">
                      Matched through another spelling of this address that reaches the same inbox.
                    </p>
                  )}
                  {status.suppression?.note && (
                    <p className="text-xs text-gray-400 mt-1">Note: {status.suppression.note}</p>
                  )}
                </>
              ) : (
                <p className="text-sm text-gray-300">Not unsubscribed. Marketing email can be sent where they have consented.</p>
              )}
              {status.contact && (
                <p className="text-xs text-gray-400 mt-1">
                  {status.contact.exists
                    ? `On your eDM list${status.contact.subscribed ? '' : ', not subscribed'}.`
                    : 'Not on your eDM list.'}
                </p>
              )}
            </div>
          </div>

          <div className="px-5 py-4 border-b border-gray-700">
            <p className="text-[0.6875rem] uppercase tracking-wider text-gray-400 mb-2">Consent on record</p>
            {Object.keys(status.purposes).length === 0 ? (
              <p className="text-sm text-gray-400">No consent records for this address.</p>
            ) : (
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {CONSENT_PURPOSES.filter((p) => status.purposes[p]).map((p) => {
                  const s = status.purposes[p]!;
                  return (
                    <li key={p} className="flex items-center gap-2 text-sm">
                      {s.granted ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <XCircle className="w-4 h-4 text-gray-500" />}
                      <span>{CONSENT_PURPOSE_LABELS[p]}</span>
                      <span className="text-xs text-gray-500">
                        {s.granted ? 'given' : 'withdrawn'}
                        {s.atMs ? ` ${formatWhen(s.atMs)}` : ''}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="px-5 py-4 space-y-3">
            <p className="text-sm font-medium">{status.suppressed ? 'Record another request' : 'Record an unsubscribe'}</p>
            <fieldset>
              <legend className="block text-xs font-semibold text-gray-400 mb-1">How did they ask?</legend>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {UNSUBSCRIBE_REASONS.map((r) => (
                  <label key={r}
                    className={`rounded-md border px-3 py-2 text-sm cursor-pointer ${reason === r ? 'border-blue-500/60 bg-blue-500/10' : 'border-gray-700 hover:bg-gray-800/60'}`}>
                    <input type="radio" name="unsub-reason" value={r} checked={reason === r} onChange={() => setReason(r)} className="mr-2" />
                    {UNSUBSCRIBE_REASON_LABELS[r]}
                  </label>
                ))}
              </div>
            </fieldset>
            <div>
              <label htmlFor="unsub-note" className="block text-xs font-semibold text-gray-400 mb-1">
                Note {reason === 'other' ? '(required)' : '(optional)'}
              </label>
              <textarea id="unsub-note" rows={2} maxLength={UNSUBSCRIBE_NOTE_MAX} value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="For example: called the office on 23 October"
                className="w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-sm" />
            </div>
            {recordError && <p role="alert" className="text-xs text-red-400">{recordError}</p>}
            <div className="flex justify-end">
              <button type="button" onClick={onRecord} disabled={recording}
                className="px-4 py-2 rounded-md bg-red-600 hover:bg-red-700 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50">
                {recording ? <Spinner size="sm" /> : <BellOff className="w-4 h-4" />}
                Record unsubscribe
              </button>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default UnsubscribesPage;
